import type { Carer } from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import type { CSSProperties, ReactNode } from 'react'
import { ProfilePicture } from '../generic'

export interface LastUpdateBoxProps {
  carer?: Carer | null
  children?: ReactNode
  comment?: string | null
  date?: string | null
  /** Grey outer + rounded inner strip (e.g. profile forms). Overrides default `lmc-card` shell. */
  formInset?: {
    outerStyle?: CSSProperties
    innerStyle?: CSSProperties
  }
  noDateText?: string
  noTitle?: boolean
  rightChildren?: ReactNode
  /** Applied to the `lmc-card` wrapper, or to the form-inset outer wrapper when `formInset` is set. */
  style?: CSSProperties
  title?: string
}

type DisplayProps = {
  carer?: Carer | null
  children?: ReactNode
  comment?: string | null
  displayDate: string
  displayTitle: string
  hasDate: boolean
  noTitle?: boolean
  rightChildren?: ReactNode
}

function getLastUpdateDisplay(
  date: string | null | undefined,
  noDateText: string | undefined,
  title: string | undefined
) {
  const hasDate = !!date
  const dateTime = date ? dayjs(date).format('DD-MM-YYYY HH:mm') : ''
  const displayTitle = title || 'Last updated'
  const displayNoDate = noDateText || 'No updates yet'
  const displayDate = hasDate ? dateTime : displayNoDate
  return { hasDate, displayTitle, displayDate }
}

function LastUpdateBoxContent({
  carer,
  children,
  comment,
  displayDate,
  displayTitle,
  hasDate,
  noTitle,
  rightChildren
}: DisplayProps) {
  return (
    <>
      <div style={styles.lastReview}>
        {!noTitle && <span style={styles.title}>{displayTitle}</span>}
        <div style={styles.dateContainer}>
          <Icon customIcon={IconType.Calendar} style={styles.calendarIcon} />
          <span id="last-review-date" style={styles.date}>
            {displayDate}
          </span>
        </div>
        {carer && hasDate && (
          <div style={styles.carerContainer}>
            <ProfilePicture
              id="carer-picture"
              url={carer.picture}
              style={styles.carerImage}
              type="handover__small"
            />
            <span style={styles.carerName} id="carer-name">
              {carer.name.first} {carer.name.last}
            </span>
          </div>
        )}
        {rightChildren && rightChildren}
      </div>
      {children}
      {comment ? (
        <div style={styles.commentContainer}>
          <div style={styles.commentTitle}>Comment</div>
          <div style={styles.commentText as CSSProperties}>{comment}</div>
        </div>
      ) : null}
    </>
  )
}

function LastUpdateBox({
  noTitle,
  carer,
  date,
  noDateText,
  style,
  title,
  rightChildren,
  comment,
  formInset,
  children
}: LastUpdateBoxProps) {
  const { hasDate, displayTitle, displayDate } = getLastUpdateDisplay(
    date,
    noDateText,
    title
  )

  const content = (
    <LastUpdateBoxContent
      carer={carer}
      children={children}
      comment={comment}
      displayDate={displayDate}
      displayTitle={displayTitle}
      hasDate={hasDate}
      noTitle={noTitle}
      rightChildren={rightChildren}
    />
  )

  if (formInset) {
    return (
      <div
        style={{
          ...styles.formInsetOuter,
          ...style,
          ...formInset.outerStyle
        }}
      >
        <div style={{ ...styles.formInsetInner, ...formInset.innerStyle }}>
          {content}
        </div>
      </div>
    )
  }

  return (
    <div
      className="lmc-card"
      style={{
        ...styles.mainContainer,
        ...((rightChildren || children) && { alignContent: 'initial' }),
        ...style
      }}
    >
      {content}
    </div>
  )
}

const styles = {
  formInsetOuter: {
    width: '100%',
    boxSizing: 'border-box' as const,
    marginTop: 8,
    paddingInline: 0
  },
  formInsetInner: {
    backgroundColor: '#F8F9FA',
    borderRadius: 16,
    padding: '8px 12px'
  },
  carerContainer: {
    display: 'flex',
    margin: '3px 0px'
  },
  calendarIcon: { width: 33, height: 33 },
  carerImage: {
    alignSelf: 'center'
  },
  carerName: {
    alignSelf: 'center',
    paddingLeft: 10
  },
  commentContainer: {
    display: 'flex',
    flexFlow: 'row nowrap',
    minHeight: 20,
    margin: '16px 0px 10px 0px',
    alignItems: 'flex-start'
  },
  commentTitle: {
    alignSelf: 'flex-start',
    display: 'block',
    fontWeight: 'bold',
    margin: '0px 20px 0px 0px'
  },
  commentText: {
    paddingLeft: 20,
    whiteSpace: 'pre-wrap'
  },
  date: {
    alignSelf: 'center',
    display: 'block',
    paddingLeft: 15,
    color: '#000',
    opacity: 0.7
  },
  dateContainer: {
    display: 'flex',
    margin: '3px 50px 3px 0px'
  },
  dateIcon: {
    alignSelf: 'center',
    marginLeft: 5
  },
  mainContainer: {
    display: 'flex',
    flexFlow: 'column wrap',
    alignContent: 'space-between',
    padding: '5px 25px'
  },
  test: {
    alignSelf: 'center'
  },
  title: {
    alignSelf: 'center',
    display: 'block',
    fontWeight: 'bold',
    margin: '3px 20px 3px 0px'
  },
  lastReview: {
    display: 'flex',
    flexFlow: 'row wrap',
    minHeight: 42
  }
}

export default LastUpdateBox
